// src/oura/ui/escapeLayer.ts
//
// Escape closes the TOP layer only.
//
// The answer screenshot opens a lightbox inside the question drawer, and both
// used to listen for Escape on the window, so one key press closed the
// lightbox and the drawer behind it together. Every layer now registers here
// instead; the one registered last is the one Escape closes, and the key is
// consumed there.
//
// A component that handles Escape itself (a Radix menu, a native select) and
// calls preventDefault keeps the key: the layer under it does not close too.

import { useEffect, useRef } from "react";

interface Layer {
    run: () => void;
}

const stack: Layer[] = [];

function onKeyDown(e: KeyboardEvent): void {
    if (e.key !== "Escape" || e.defaultPrevented) return;
    const top = stack[stack.length - 1];
    if (!top) return;
    e.preventDefault();
    top.run();
}

/**
 * Register an Escape handler while `active`. Layers stack in the order they
 * became active, so a lightbox opened inside a drawer closes first.
 */
export function useEscapeLayer(onEscape: () => void, active: boolean): void {
    // The latest handler, read at key time: re-registering on every render
    // would move this layer to the top of the stack each time its owner
    // re-rendered, above a lightbox opened after it.
    const handler = useRef(onEscape);
    useEffect(() => {
        handler.current = onEscape;
    }, [onEscape]);

    useEffect(() => {
        if (!active) return;
        const layer: Layer = { run: () => handler.current() };
        stack.push(layer);
        if (stack.length === 1) window.addEventListener("keydown", onKeyDown);
        return () => {
            const i = stack.lastIndexOf(layer);
            if (i >= 0) stack.splice(i, 1);
            if (stack.length === 0) window.removeEventListener("keydown", onKeyDown);
        };
    }, [active]);
}
